import { observable, runInAction } from "mobx"

import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { projectWarmupSnapshotsQueryKey } from "@/lib/queries.ts"

import type { PR_Project2, TID, WarmupSnapshot } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("WarmupSnapshotsViewModel")


export class WarmupSnapshotsViewModel extends Model {
  snapshots = observable.box<WarmupSnapshot[]>([])
  error = observable.box<string | null>(null)

  constructor(
    readonly context: OrgContext,
    readonly canEditProject: IObservableValue<boolean>,
    private readonly project: PR_Project2
  ) {
    super("WarmupSnapshotsViewModel")

    void this.load()
  }

  get projectKey() {
    return this.project.key
  }

  get repository() {
    return this.project.repository
  }

  async load() {
    this.isLoading.set(true)
    this.error.set(null)

    try {
      const snapshots = await withGlobalLoader(this.context, this.context.queries.listProjectWarmupSnapshots(this.projectKey, this.repository.name))

      runInAction(() => {
        this.snapshots.set(snapshots)
        this.isLoading.set(false)
      })
    } catch (error) {
      const errorMessage = "Failed to load warmup snapshots:"
      runInAction(() => {
        this.error.set(`${errorMessage} ${error}`)
        this.isLoading.set(false)
      })
      log.error(errorMessage, error)
    }
  }

  createSnapshot = async () => {
    const newSnapshot = await withGlobalLoader(this.context, this.context.queries.apiClient.createProjectWarmupSnapshot(this.projectKey, this.repository.name))

    runInAction(() => this.snapshots.set([newSnapshot, ...this.snapshots.get()]))

    void this.context.queryClient.invalidateQueries({ queryKey: projectWarmupSnapshotsQueryKey(this.projectKey) })
  }

  deleteSnapshot = async (snapshotId: string) => {
    const newSnapshots = this.snapshots.get().filter((snapshot) => snapshot.id !== snapshotId)
    runInAction(() => this.snapshots.set(newSnapshots))

    await withGlobalLoader(this.context, this.context.queries.apiClient.deleteProjectWarmupSnapshot(this.projectKey, this.repository.name, snapshotId as TID))
    void this.context.queryClient.invalidateQueries({ queryKey: projectWarmupSnapshotsQueryKey(this.projectKey) })
  }
}
